// src/pages/KitsPage/KitsPage.tsx
import React, { useState, useMemo, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { API_BASE_URL } from '../../config';
import KitExplorer from '../../components/KitExplorer/KitExplorer';
import KitCard from '../../components/KitCard/KitCard';

import 'swiper/css';
import 'swiper/css/pagination';
import styles from './KitsPage.module.css';

const useIsMobile = (breakpoint = 999) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);
  return isMobile;
};

export interface Kit {
  id: string;
  name: string;
  category: string;
  title: string;
  description: string;
  audioUrl?: string;
  imageUrl: string;
  price?: string;
}

const KitsPage: React.FC = () => {
  const [allKits, setAllKits] = useState<Kit[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All Kits');
  const [menuSwiper, setMenuSwiper] = useState<SwiperType | null>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/products/drum-kits`)
      .then(res => res.ok ? res.json() : Promise.reject())
      .then((data: any[]) => {
        const kits: Kit[] = data.map(k => ({
          id: k.id,
          name: k.name?.toLowerCase().replace(/\s+/g, '-') || k.id,
          category: k.category || 'Uncategorized',
          title: k.name,
          description: k.description || '',
          audioUrl: k.audioPreviewUrl || undefined,
          imageUrl: k.imageUrl || '/placeholder-kit.png',
          price: k.price,
        }));
        setAllKits(kits);
      })
      .catch(() => setAllKits([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const cats = new Set(allKits.map(k => k.category));
    return ['All Kits', ...Array.from(cats).sort()];
  }, [allKits]);

  const filteredKits = useMemo(() => {
    if (selectedCategory === 'All Kits') return allKits;
    return allKits.filter(kit => kit.category === selectedCategory);
  }, [selectedCategory, allKits]);

  const handleCategoryClick = (category: string, index: number) => {
    setSelectedCategory(category);
    if (isMobile) menuSwiper?.slideTo(index);
  };

  if (loading) {
    return (
      <main className={styles.pageContainer}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
          <p>Carregando kits...</p>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.pageContainer}>
      <div className={styles.sideMenuColumn}>
        {isMobile ? (
          <Swiper
            className={styles.menuSwiperContainer} onSwiper={setMenuSwiper} slidesPerView={'auto'} centeredSlides={true} spaceBetween={10} slideToClickedSlide={true}
            onSlideChange={(swiper: SwiperType) => setSelectedCategory(categories[swiper.realIndex])}
          >
            {categories.map((category, index) => (
              <SwiperSlide key={category} className={styles.menuSwiperSlide}>
                <button className={`${styles.menuButton} ${selectedCategory === category ? styles.active : ''}`} onClick={() => handleCategoryClick(category, index)}>
                  {category}
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <ul className={styles.navList}>
            {categories.map((category, index) => (
              <li key={category}>
                <button className={`${styles.menuButton} ${selectedCategory === category ? styles.active : ''}`} onClick={() => handleCategoryClick(category, index)}>
                  {category}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={styles.mainContent}>
        {filteredKits.length === 0 ? (
          <div className={styles.emptyState}>
            <span className="material-symbols-outlined" style={{ fontSize: '3rem', opacity: 0.3 }}>library_music</span>
            <p>Nenhum kit encontrado nesta categoria.</p>
          </div>
        ) : isMobile ? (
          <Swiper
            key={selectedCategory}
            modules={[Pagination]}
            slidesPerView={1}
            spaceBetween={20}
            pagination={{ clickable: true }}
            className={styles.kitsSwiperContainer}
          >
            {filteredKits.map(kit => (
              <SwiperSlide key={kit.id} className={styles.kitSlide}>
                <KitCard kit={kit} />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <KitExplorer kitsToDisplay={filteredKits} />
        )}
      </div>
    </main>
  );
};

export default KitsPage;